import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdOutlineAdd, MdOutlineVisibility, MdEdit } from 'react-icons/md';
import {
  FaTrash,
  FaCheckCircle,
  FaRegCheckCircle,
  FaExclamationCircle,
  FaRegCircle,
  FaBan,
  FaTimesCircle,
  FaUpload,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchAssetsApi, AssetListItem, deleteAssetApi, createBulkAssetsWithNamesApi, BulkAssetWithNames } from 'src/services/assetsServices';
import * as ExcelJS from 'exceljs';

const AssetsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [assets, setAssets] = useState<AssetListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteName, setConfirmDeleteName] = useState<string | null>(null);
  
  // Bulk upload state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkSource, setBulkSource] = useState('');
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAssets = async () => {
    setLoading(true);
    try {
      console.log('loadAssets called with:', { page, pageSize, activeSearchTerm });
      const result = await fetchAssetsApi({
        page,
        perPage: pageSize,
        query: activeSearchTerm.trim(),
      });
      console.log('loadAssets result:', result);
      setAssets(result.data);
      setTotalPages(result.totalPages);
      setTotalRecords(result.total);
    } catch (error: any) {
      console.error('loadAssets error:', error);
      toast.error(error.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeSearchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Search submitted with term:', searchTerm);
    setActiveSearchTerm(searchTerm);
    setPage(1);
  };

  const openDeleteModal = (id: string, name: string) => {
    setConfirmDeleteId(id);
    setConfirmDeleteName(name);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;

    setDeletingId(confirmDeleteId);
    try {
      await deleteAssetApi(confirmDeleteId);
      toast.success(t('assets.delete.success', { defaultValue: 'Asset deleted successfully' }));
      // Reload current page
      loadAssets();
    } catch (error: any) {
      toast.error(error.message || t('assets.delete.failed', { defaultValue: 'Failed to delete asset' }));
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
      setConfirmDeleteName(null);
    }
  };

  // Bulk upload functions
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setBulkFile(e.target.files[0]);
    }
  };

  const readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result;
        if (result instanceof ArrayBuffer) {
          const bytes = new Uint8Array(result);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) {
            const byte = bytes[i];
            if (byte !== undefined) {
              binary += String.fromCharCode(byte);
            }
          }
          const base64 = btoa(binary);
          resolve(base64);
        } else {
          reject(new Error('Invalid file format'));
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const parseFileToAssets = async (content: string): Promise<BulkAssetWithNames[]> => {
    const workbook = new ExcelJS.Workbook();
    const buffer = Uint8Array.from(atob(content), c => c.charCodeAt(0));
    
    try {
      await workbook.xlsx.load(buffer as any);
    } catch (error: any) {
      throw new Error(`Failed to read Excel file. Please ensure the file is a valid .xlsx file. Error: ${error.message || 'Unknown error'}`);
    }
    
    // Read Asset Types from Sheet 1
    const assetTypesSheet = workbook.getWorksheet('Asset Types');
    if (!assetTypesSheet) {
      throw new Error('Asset Types sheet not found in the Excel file');
    }
    
    const assetTypeFields: { [key: string]: Array<{ label: string; type: string; required: boolean }> } = {};
    
    assetTypesSheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header row
      
      const assetTypeName = row.getCell(1).value?.toString() || '';
      const fieldLabel = row.getCell(2).value?.toString() || '';
      const fieldType = row.getCell(3).value?.toString() || 'string';
      const required = row.getCell(4).value?.toString()?.toLowerCase() === 'true';
      
      if (assetTypeName && fieldLabel) {
        if (!assetTypeFields[assetTypeName]) {
          assetTypeFields[assetTypeName] = [];
        }
        assetTypeFields[assetTypeName].push({
          label: fieldLabel,
          type: fieldType,
          required: required
        });
      }
    });
    
    // Read Assets from Sheet 2
    const assetsSheet = workbook.getWorksheet('Assets');
    if (!assetsSheet) {
      throw new Error('Assets sheet not found in the Excel file');
    }
    
    const assets: BulkAssetWithNames[] = [];
    const errors: string[] = [];
    
    // Get the header row to understand column mapping
    const headerRow = assetsSheet.getRow(1);
    const headerMap: { [key: string]: number } = {};
    
    // Map header columns (starting from column 8 for dynamic fields)
    headerRow.eachCell((cell, colNumber) => {
      if (colNumber >= 8) { // Dynamic fields start from column 8
        const headerValue = cell.value?.toString() || '';
        if (headerValue) {
          headerMap[headerValue] = colNumber;
        }
      }
    });
    
    assetsSheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header row
      
      const assetName = row.getCell(1).value?.toString() || '';
      const assetTypeName = row.getCell(2).value?.toString() || '';
      const ownerName = row.getCell(3).value?.toString() || '';
      const custodianName = row.getCell(4).value?.toString() || '';
      const posture = row.getCell(5).value?.toString() || 'High';
      const compliance = row.getCell(6).value?.toString() || 'ISO 27001';
      const date = row.getCell(7).value?.toString() || new Date().toISOString().split('T')[0] || '';
      
      if (assetName && assetTypeName) {
        const fieldsForType = assetTypeFields[assetTypeName] || [];
        
        // Validate that asset type exists
        if (fieldsForType.length === 0) {
          errors.push(`Row ${rowNumber}: Asset type "${assetTypeName}" not found in Asset Types sheet`);
          return;
        }
        
        const asset: BulkAssetWithNames = {
          assetName,
          assetTypeName,
          assetTypeFields: fieldsForType,
          ownerName,
          custodianName,
          posture,
          compliance,
          date,
          fields: {},
        };
        
        // Map fields dynamically based on header mapping
        fieldsForType.forEach((field) => {
          const columnNumber = headerMap[field.label];
          
          if (columnNumber) {
            const cellValue = row.getCell(columnNumber).value;
            
            // Validate required fields
            if (field.required && (cellValue === null || cellValue === undefined || cellValue.toString().trim() === '')) {
              errors.push(`Row ${rowNumber}: Required field "${field.label}" is missing for asset "${assetName}"`);
              return;
            }
            
            // Process field value
            if (cellValue !== null && cellValue !== undefined) {
              let processedValue: any = cellValue;
              
              // Convert based on field type
              if (field.type === 'number') {
                const numValue = typeof cellValue === 'number' ? cellValue : parseInt(cellValue.toString());
                if (isNaN(numValue)) {
                  errors.push(`Row ${rowNumber}: Field "${field.label}" must be a number for asset "${assetName}"`);
                  return;
                }
                processedValue = numValue;
              } else if (field.type === 'boolean') {
                processedValue = cellValue.toString().toLowerCase() === 'true';
              } else {
                processedValue = cellValue.toString().trim();
              }
              
              asset.fields[field.label] = processedValue;
            }
          }
        });
        
        assets.push(asset);
      }
    });
    
    if (errors.length > 0) {
      throw new Error(`Validation errors found:\n${errors.join('\n')}`);
    }
    
    return assets;
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error(t('assets.bulk.toast.noFile') || 'Please select a file first');
      return;
    }

    setIsUploading(true);
    try {
      const fileContent = await readFileContent(bulkFile);
      const assets = await parseFileToAssets(fileContent);

      if (assets.length === 0) {
        toast.error(t('assets.bulk.toast.noValid') || 'No valid assets found in the file. Please check the format and try again.');
        return;
      }

      await createBulkAssetsWithNamesApi({ assets });
      toast.success(t('assets.bulk.toast.success', { count: assets.length }) || `Successfully created ${assets.length} assets`);
      setIsBulkModalOpen(false);
      setBulkFile(null);
      loadAssets(); // Refresh the table after upload
    } catch (error: any) {
      if (error.message.includes('Validation errors found:')) {
        toast.error(t('assets.bulk.toast.validationFailed') || 'File validation failed. Please check the errors and fix them.');
        console.error('Validation errors:', error.message);
      } else {
        toast.error(error.message || t('assets.bulk.toast.failed') || 'Failed to upload assets');
      }
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    const workbook = new ExcelJS.Workbook();
    
    // Sheet 1: Asset Types
    const assetTypesSheet = workbook.addWorksheet('Asset Types');
    assetTypesSheet.columns = [
      { header: 'Asset Type Name', key: 'assetTypeName', width: 20 },
      { header: 'Field Label', key: 'fieldLabel', width: 20 },
      { header: 'Field Type', key: 'fieldType', width: 15 },
      { header: 'Required', key: 'required', width: 10 },
    ];
    assetTypesSheet.addRow(['User', 'Application Name', 'string', 'true']);
    assetTypesSheet.addRow(['User', 'Email', 'string', 'true']);
    assetTypesSheet.addRow(['Service', 'Service URL', 'string', 'true']);
    assetTypesSheet.addRow(['Service', 'Port', 'number', 'false']);
    
    // Sheet 2: Assets
    const assetsSheet = workbook.addWorksheet('Assets');
    assetsSheet.columns = [
      { header: 'Asset Name', key: 'assetName', width: 20 },
      { header: 'Asset Type Name', key: 'assetTypeName', width: 20 },
      { header: 'Owner Name', key: 'ownerName', width: 20 },
      { header: 'Custodian Name', key: 'custodianName', width: 20 },
      { header: 'Posture', key: 'posture', width: 15 },
      { header: 'Compliance', key: 'compliance', width: 20 },
      { header: 'Date', key: 'date', width: 15 },
      { header: 'Application Name', key: 'applicationName', width: 20 },
    ];
    assetsSheet.addRow(['John Doe', 'User', 'IT Department', 'IT Department', 'High', 'ISO 27001', new Date().toISOString().split('T')[0], 'CRM System']);
    
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'asset_bulk_upload_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem active style={{ paddingRight: '10px' }}>
          {t('assets.titlePage') || 'Assets Management'}
        </BreadcrumbItem>
      </Breadcrumb>

      <h4 className="mb-lg px-2">
        {t('assets.titlePage') || 'Assets Management'}
      </h4>

      <div
        className="container_info"
        style={{ padding: '40px 20px', backgroundColor: 'var(--widget-bg)', zoom: 0.9 }}
      >
        <form
          className="d-flex justify-content-between my-2 align-items-center"
          onSubmit={handleSearch}
        >
          <div className="d-flex gap-2 align-items-center">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(e as any);
                }
              }}
              placeholder={t('assets.searchPlaceholder') || 'Search assets...'}
              className="search-input"
              style={{ maxWidth: 500, minWidth: 300 }}
            />
            <button
              type="submit"
              className="btn btn-outline-primary mb-3"
              disabled={loading}
            >
              {t('assets.search') || 'Search'}
            </button>
          </div>
          <div className="d-flex gap-2">
            {activeSearchTerm && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setActiveSearchTerm('');
                  setPage(1);
                }}
                disabled={loading}
              >
                {t('common.clear') || 'Clear'}
              </button>
            )}
            <button
              type="button"
              className="submit-button4"
              onClick={() => navigate('/assets/create')}
            >
              <MdOutlineAdd size={20} className="me-1" />
              {t('assets.Create Asset') || 'Create Asset'}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => setIsBulkModalOpen(true)}
              style={{ marginLeft: '10px' }}
            >
              <FaUpload size={18} className="me-1" />
              {t('assets.Bulk Create') || 'Bulk Create'}
            </button>
          </div>
        </form>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <span>
            {(t('assets.totalRecords') || 'Total records:') + ' '}
            <strong>{totalRecords}</strong>
          </span>
          <div className="d-flex align-items-center gap-2">
            <span>{t('question.page') || 'Page'} {page} {t('question.to') || 'of'} {totalPages}</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 80 }}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>
        </div>

        <table className="styled-table">
          <thead className="table-dark">
            <tr>
              <th style={{ width: '10%' }}>
                {t('assets.headers.action', { defaultValue: 'Action' })}
              </th>
              <th style={{ width: '18%' }}>
                {t('assets.headers.assetName', { defaultValue: 'Asset Name' })}
              </th>
              <th style={{ width: '12%' }}>
                {t('assets.headers.type', { defaultValue: 'Type' })}
              </th>
              <th style={{ width: '12%' }}>
                {t('assets.headers.owner', { defaultValue: 'Owner' })}
              </th>
              <th style={{ width: '12%' }}>
                {t('assets.headers.custodian', { defaultValue: 'Custodian' })}
              </th>
              <th style={{ width: '10%' }}>
                {t('assets.headers.posture', { defaultValue: 'Posture' })}
              </th>
              <th style={{ width: '13%' }}>
                {t('assets.headers.compliance', { defaultValue: 'Compliance' })}
              </th>
              <th style={{ width: '13%' }}>
                {t('assets.headers.date', { defaultValue: 'Date' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-4">
                  {t('common.loading') || 'Loading...'}
                </td>
              </tr>
            ) : assets.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-4">
                  {t('assets.noData') || 'No assets found'}
                </td>
              </tr>
            ) : (
              assets.map((asset) => (
                <tr key={asset.id}>
                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => navigate(`/assets/${asset.id}`)}
                      >
                        <MdOutlineVisibility size={18} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => navigate(`/assets/edit/${asset.id}`)}
                        style={{
                          backgroundColor: 'transparent',
                          borderColor: '#f97316',
                          color: '#f97316',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f97316';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#f97316';
                        }}
                      >
                        <MdEdit size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => openDeleteModal(asset.id, asset.assetName)}
                        disabled={deletingId === asset.id}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                  <td>{asset.assetName}</td>
                  <td>{asset.assetType?.name || '-'}</td>
                  <td>{asset.owner?.name || '-'}</td>
                  <td>{asset.custodian?.name || '-'}</td>
                  <td>
                    {asset.posture ? (
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor:
                            asset.posture === 'High'
                              ? '#fee2e2'
                              : asset.posture === 'Medium'
                              ? '#fef3c7'
                              : asset.posture === 'Low'
                              ? '#d1fae5'
                              : '#f3f4f6',
                          color:
                            asset.posture === 'High'
                              ? '#dc2626'
                              : asset.posture === 'Medium'
                              ? '#d97706'
                              : asset.posture === 'Low'
                              ? '#059669'
                              : '#6b7280',
                        }}
                      >
                        {asset.posture}
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {asset.compliance ? (
                      <div
                        style={{
                          fontWeight: 600,
                          borderRadius: '12px',
                          border: `1px solid ${
                            asset.compliance === 'Fully Implemented'
                              ? '#1E7E34'
                              : asset.compliance === 'Implemented'
                              ? '#1565C0'
                              : asset.compliance === 'Mostly Implemented'
                              ? '#FFC107'
                              : asset.compliance === 'Partially Implemented'
                              ? '#F3903F'
                              : asset.compliance === 'Averagely Implemented'
                              ? '#6C757D'
                              : asset.compliance === 'Not Implemented'
                              ? '#DC3545'
                              : '#ccc'
                          }`,
                          color:
                            asset.compliance === 'Fully Implemented'
                              ? '#1E7E34'
                              : asset.compliance === 'Implemented'
                              ? '#1565C0'
                              : asset.compliance === 'Mostly Implemented'
                              ? '#FFC107'
                              : asset.compliance === 'Partially Implemented'
                              ? '#F3903F'
                              : asset.compliance === 'Averagely Implemented'
                              ? '#6C757D'
                              : asset.compliance === 'Not Implemented'
                              ? '#DC3545'
                              : '#333',
                          backgroundColor:
                            asset.compliance === 'Fully Implemented'
                              ? '#E8F5E9'
                              : asset.compliance === 'Implemented'
                              ? '#E3F2FD'
                              : asset.compliance === 'Mostly Implemented'
                              ? '#FFF8E1'
                              : asset.compliance === 'Partially Implemented'
                              ? '#FFF3E0'
                              : asset.compliance === 'Averagely Implemented'
                              ? '#F5F5F5'
                              : asset.compliance === 'Not Implemented'
                              ? '#FFEBEE'
                              : '#fff',
                          display: 'inline-flex',
                          gap: '8px',
                          alignItems: 'center',
                          padding: '6px 12px',
                        }}
                      >
                        {asset.compliance === 'Fully Implemented' && (
                          <FaCheckCircle size={18} />
                        )}
                        {asset.compliance === 'Implemented' && (
                          <FaRegCheckCircle size={18} />
                        )}
                        {asset.compliance === 'Mostly Implemented' && (
                          <FaExclamationCircle size={18} />
                        )}
                        {asset.compliance === 'Partially Implemented' && (
                          <FaRegCircle size={18} />
                        )}
                        {asset.compliance === 'Averagely Implemented' && (
                          <FaBan size={18} />
                        )}
                        {asset.compliance === 'Not Implemented' && (
                          <FaTimesCircle size={18} />
                        )}
                        {asset.compliance}
                      </div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {asset.date ? asset.date.split('T')[0] : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="pagination-controls mt-4 d-flex gap-2 align-items-center">
          <button
            className="btn btn-warning btn-sm text-white"
            onClick={() => setPage(1)}
            disabled={page === 1}
          >
            {'<<'}
          </button>
          <button
            className="btn btn-warning btn-sm text-white"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
          >
            {'<'}
          </button>
          <button
            className="btn btn-warning btn-sm text-white"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages || 1))}
            disabled={page >= totalPages}
          >
            {'>'}
          </button>
          <button
            className="btn btn-warning btn-sm text-white"
            onClick={() => setPage(totalPages || 1)}
            disabled={page >= totalPages}
          >
            {'>>'}
          </button>
        </div>

        {/* Delete confirmation modal */}
        {confirmDeleteId && (
          <div
            className="modal fade show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {t('assets.delete.title', { defaultValue: 'Delete Asset' })}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => {
                      if (deletingId) return;
                      setConfirmDeleteId(null);
                      setConfirmDeleteName(null);
                    }}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    {t('assets.delete.confirm', {
                      defaultValue: 'Are you sure you want to delete this asset?',
                    })}
                  </p>
                  {confirmDeleteName && (
                    <p>
                      <strong>{confirmDeleteName}</strong>
                    </p>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={!!deletingId}
                    onClick={() => {
                      if (deletingId) return;
                      setConfirmDeleteId(null);
                      setConfirmDeleteName(null);
                    }}
                  >
                    {t('common.cancel', { defaultValue: 'Cancel' })}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleConfirmDelete}
                    disabled={!!deletingId}
                  >
                    {deletingId
                      ? t('assets.delete.deleting', { defaultValue: 'Deleting...' })
                      : t('assets.delete.confirmButton', { defaultValue: 'Delete' })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

        {/* Bulk Upload Modal */}
        <Modal isOpen={isBulkModalOpen} toggle={() => {
          setIsBulkModalOpen(false);
          setBulkSource('');
          setBulkFile(null);
        }} size="lg">
          <ModalHeader toggle={() => {
            setIsBulkModalOpen(false);
            setBulkSource('');
            setBulkFile(null);
          }}>
            {t('assets.bulk.title') || 'Bulk Create Assets'}
          </ModalHeader>
          <ModalBody>
            <div className="mb-4">
              <label className="form-label d-block mb-2">
                <strong>{t('assets.bulk.methodLabel') || 'Select Method:'}</strong>
              </label>
              <select
                className="form-select"
                value={bulkSource}
                onChange={(e) => {
                  setBulkSource(e.target.value);
                  if (e.target.value === 'dataSource') {
                    setIsBulkModalOpen(false);
                    navigate('/dataresource');
                  }
                }}
              >
                <option value="">{t('assets.bulk.selectMethod') || '-- Select Method --'}</option>
                <option value="dataSource">{t('assets.bulk.dataSource') || 'Data Source'}</option>
                <option value="uploadFile">{t('assets.bulk.uploadFile') || 'Upload File'}</option>
              </select>
            </div>

            {bulkSource === 'uploadFile' && (
              <>
                <div className="mb-3 p-3 bg-light rounded">
                  <p className="text-primary mb-2">
                    <strong>{t('assets.bulk.sheetInfoTitle') || 'Excel File Structure:'}</strong>
                  </p>
                  <ul className="text-primary mb-2 ms-3">
                    <li>
                      <strong>{t('assets.bulk.sheet1')?.split(':')[0] || 'Sheet 1 (Asset Types)'}:</strong>{' '}
                      {t('assets.bulk.sheet1')?.split(': ')[1] || 'Define asset types and their fields'}
                    </li>
                    <li>
                      <strong>{t('assets.bulk.sheet2')?.split(':')[0] || 'Sheet 2 (Assets)'}:</strong>{' '}
                      {t('assets.bulk.sheet2')?.split(': ')[1] || 'List all assets to be created'}
                    </li>
                  </ul>
                  <p className="text-primary mb-0">
                    <strong>{t('assets.bulk.important') || 'Important:'}</strong>{' '}
                    {t('assets.bulk.sheetNote') || 'Ensure asset types are defined in Sheet 1 before adding assets in Sheet 2'}
                  </p>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    {t('assets.bulk.selectExcel') || 'Select Excel File'}
                  </label>
                  <div className="border border-dashed border-secondary rounded p-4 text-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="d-none"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      disabled={isUploading}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      {bulkFile ? bulkFile.name : (t('assets.bulk.selectExcel') || 'Select Excel File')}
                    </button>
                    <p className="text-muted mt-2 mb-0 small">
                      {t('assets.bulk.supports') || 'Supports .xlsx and .xls formats'}
                    </p>
                  </div>
                </div>

                <div className="mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={downloadTemplate}
                    disabled={isUploading}
                  >
                    {t('assets.bulk.downloadTemplate') || 'Download Template'}
                  </button>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => {
              setIsBulkModalOpen(false);
              setBulkSource('');
              setBulkFile(null);
            }} disabled={isUploading}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            {bulkSource === 'uploadFile' && (
              <Button
                color="success"
                onClick={handleBulkUpload}
                disabled={!bulkFile || isUploading}
              >
                {isUploading
                  ? (t('assets.bulk.uploading') || 'Uploading...')
                  : (t('assets.bulk.upload') || 'Upload')}
              </Button>
            )}
          </ModalFooter>
        </Modal>
    </div>
  );
};

export default AssetsList;


